import React, { memo, useCallback, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { MenuIcon } from '@sharedCR/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import usePopover from 'hooks/usePopover';
import { getConditionalArray } from 'utils/data';
import { naturalSortBy } from 'utils/sort';

import { FilterTypes } from '../../../coreDesignSystem/Table/FilterBaseCDS/FilterBaseCDS.constants';

import { SEARCH_THRESHOLD, SECTION_DIVIDER_KEY } from './ColumnSelectFilterMenu.constants';
import {
  anchorOrigin,
  filterIconButtonStyles,
  getColorDotStyles,
  menuControlStyles,
  menuItemStyles,
  menuListStyles,
  noResultsStyles,
  optionCheckboxStyles,
  optionLabelStyles,
  popoverSlotProps,
  searchBoxStyles,
  searchInputStyles,
  sectionDividerStyles,
  selectedCountStyles,
  transformOrigin,
} from './ColumnSelectFilterMenu.styles';
import { ColumnSelectFilterMenuTestIds } from './ColumnSelectFilterMenu.testIds';

const ColumnSelectFilterMenu = ({
  columnKey,
  selectOptions = [],
  filterType = FilterTypes.MULTI_SELECT,
  filterValues = [],
  onFilterChange,
  externalAnchorEl,
  onClose: onExternalClose,
}) => {
  const { anchorEl: internalAnchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const isControlled = !!externalAnchorEl;
  const anchorEl = isControlled ? externalAnchorEl : internalAnchorEl;

  const [selectedValues, setSelectedValues] = useState(filterValues);
  const [searchQuery, setSearchQuery] = useState('');

  const isOpen = !!anchorEl;
  const isFilterActive = !!filterValues.length;
  const showSearch = selectOptions.length >= SEARCH_THRESHOLD;

  const iconButtonClassName = useMemo(
    () => `column-filter-icon${isFilterActive ? ' filter-active' : ''}`,
    [isFilterActive],
  );

  const sortedOptions = useMemo(() => {
    const lowerQuery = searchQuery.toLowerCase();
    const filtered = searchQuery
      ? selectOptions.filter(opt => opt.label.toLowerCase().includes(lowerQuery))
      : selectOptions;
    const sorted = naturalSortBy(filtered, 'label');

    const selected = sorted.filter(opt => selectedValues.includes(opt.value));
    const unselected = sorted.filter(opt => !selectedValues.includes(opt.value));

    return { selected, unselected, hasResults: !!sorted.length };
  }, [selectOptions, selectedValues, searchQuery]);

  const handleOpen = useCallback(
    e => {
      e.stopPropagation();
      setSelectedValues(filterValues);
      setSearchQuery('');
      onSetAnchorElement(e);
    },
    [filterValues, onSetAnchorElement],
  );

  const closePopover = useCallback(() => {
    if (isControlled) {
      onExternalClose();

      return;
    }

    onClearAnchorElement();
  }, [isControlled, onExternalClose, onClearAnchorElement]);

  const handleClose = useCallback(() => {
    setSelectedValues(filterValues);
    setSearchQuery('');
    closePopover();
  }, [filterValues, closePopover]);

  const getOnSelectOption = useCallback(
    value => () => {
      setSelectedValues(prev => {
        if (filterType === FilterTypes.SINGLE_SELECT) {
          return prev[0] !== value ? [value] : [];
        }

        return _.includes(prev, value) ? prev.filter(v => v !== value) : [...prev, value];
      });
    },
    [filterType],
  );

  const handleSearchChange = useCallback(({ target }) => {
    setSearchQuery(target.value);
  }, []);

  const handleSearchKeyDown = useCallback(e => {
    e.stopPropagation();
  }, []);

  const handleConfirm = useCallback(() => {
    onFilterChange(columnKey, { values: selectedValues });
    closePopover();
  }, [columnKey, selectedValues, onFilterChange, closePopover]);

  const handleClear = useCallback(() => {
    setSelectedValues([]);
    onFilterChange(columnKey, { values: [] });
    closePopover();
  }, [columnKey, onFilterChange, closePopover]);

  const renderOption = useCallback(
    ({ value, label, bg, fg }) => (
      <MenuItem
        data-testid={ColumnSelectFilterMenuTestIds.getOptionId(value)}
        key={value}
        sx={menuItemStyles}
        value={value}
        onClick={getOnSelectOption(value)}
      >
        <Checkbox
          checked={selectedValues.includes(value)}
          data-testid={ColumnSelectFilterMenuTestIds.getOptionCheckboxId(value)}
          sx={optionCheckboxStyles}
        />
        {bg ? <Box component="span" sx={getColorDotStyles(bg)} /> : null}
        <Typography color={fg} sx={optionLabelStyles} variant="regular14">
          {label}
        </Typography>
      </MenuItem>
    ),
    [selectedValues, getOnSelectOption],
  );

  const menuItems = useMemo(
    () => [
      ...sortedOptions.selected.map(renderOption),
      ...getConditionalArray(
        !!sortedOptions.selected.length && !!sortedOptions.unselected.length,
        () => [<Divider key={SECTION_DIVIDER_KEY} sx={sectionDividerStyles} />],
      ),
      ...sortedOptions.unselected.map(renderOption),
    ],
    [sortedOptions, renderOption],
  );

  return (
    <>
      {!isControlled && (
        <IconButton
          disableRipple
          className={iconButtonClassName}
          data-testid={ColumnSelectFilterMenuTestIds.TRIGGER}
          size="small"
          sx={filterIconButtonStyles}
          onClick={handleOpen}
        >
          <MenuIcon size={IconSize[16]} />
        </IconButton>
      )}
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        open={isOpen}
        slotProps={popoverSlotProps}
        transformOrigin={transformOrigin}
        onClose={handleClose}
      >
        <Box>
          {showSearch ? (
            <Box sx={searchBoxStyles}>
              <input
                autoFocus
                placeholder="Search..."
                style={searchInputStyles}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
              />
            </Box>
          ) : null}
          {!!selectedValues.length && (
            <Box sx={selectedCountStyles}>
              <Typography color="text.secondary" variant="regular12">
                {selectedValues.length} selected
              </Typography>
            </Box>
          )}
          <MenuList autoFocusItem={false} sx={menuListStyles}>
            {sortedOptions.hasResults ? (
              menuItems
            ) : (
              <Box sx={noResultsStyles}>
                <Typography color="text.secondary" variant="regular14">
                  No results
                </Typography>
              </Box>
            )}
          </MenuList>
          <Stack sx={menuControlStyles}>
            <Button
              data-testid={ColumnSelectFilterMenuTestIds.CLEAR_BUTTON}
              variant="line"
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button
              data-testid={ColumnSelectFilterMenuTestIds.CONFIRM_BUTTON}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

ColumnSelectFilterMenu.propTypes = {
  columnKey: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  externalAnchorEl: PropTypes.object,
  filterType: PropTypes.string,
  filterValues: PropTypes.array,
  selectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  onClose: PropTypes.func,
};

export default memo(ColumnSelectFilterMenu);
